/**
 * EstimateDetailPage Component
 *
 * Page for viewing a single estimate with line items,
 * export/share functionality, and navigation.
 */
import { useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  FileText,
  RefreshCw,
  AlertCircle,
  Calendar,
  User,
  Database,
} from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { EstimateBriefChat } from '@/components/estimates/EstimateBriefChat';
import { EstimateOrderView } from '@/components/estimates/EstimateOrderView';
import { EstimateManagerSelect } from '@/components/estimates/EstimateManagerSelect';
import { estimatesApi } from '@/services/api/estimates';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { EstimateStatus } from '@/types/estimate';

/**
 * Get status badge variant based on status
 */
function getStatusVariant(
  status: EstimateStatus
): 'default' | 'secondary' | 'destructive' | 'outline' | 'pending' | 'completed' {
  switch (status) {
    case 'APPROVED':
      return 'completed';
    case 'DRAFT':
      return 'secondary';
    case 'PENDING_REVIEW':
      return 'pending';
    case 'REJECTED':
      return 'destructive';
    default:
      return 'secondary';
  }
}

/**
 * Get human-readable status text
 */
function getStatusText(status: EstimateStatus): string {
  switch (status) {
    case 'APPROVED':
      return 'Согласовано';
    case 'DRAFT':
      return 'Черновик';
    case 'PENDING_REVIEW':
      return 'На согласовании';
    case 'REJECTED':
      return 'Отклонено';
    default:
      return status;
  }
}

/**
 * Format cost with thousands separator
 */
function formatCost(cost: number | string): string {
  const num = typeof cost === 'string' ? parseFloat(cost) : cost;
  return num.toLocaleString('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Format date for display
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('ru-RU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * EstimateDetailPage is the page for viewing a single estimate.
 */
export function EstimateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Fetch estimate details
  const {
    data: estimate,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['estimate', id],
    queryFn: () => estimatesApi.getById(id!),
    enabled: !!id,
  });

  // Set document title
  useEffect(() => {
    if (estimate) {
      document.title = `${estimate.display_name || 'Смета'} - SMETA AI`;
    } else {
      document.title = 'Детали сметы - SMETA AI';
    }
  }, [estimate]);

  // KAN-746: the order of an online store lives in "Заказы"; old links lead there.
  useEffect(() => {
    if (estimate?.mode === 'ecommerce') navigate(`/orders/${estimate.id}`, { replace: true });
  }, [estimate, navigate]);

  // KAN-987: списка смет больше нет. Назад по истории, без истории на дашборд.
  const handleBack = useCallback(() => {
    if (window.history.length <= 1) navigate('/');
    else navigate(-1);
  }, [navigate]);

  // Calculate totals (safe even when estimate is null)
  const lineItems = estimate?.line_items || [];
  const calculatedTotal = lineItems.reduce((sum, item) => sum + item.total_price, 0);

  return (
    <>
      <PageLayout
        data-testid="estimate-detail-page"
        titleIcon={
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
            <FileText className="h-5 w-5 text-white" />
          </div>
        }
        title={
          estimate
            ? estimate.display_name || (estimate.short_id ? `Смета #${estimate.short_id}` : estimate.query)
            : 'Детали сметы'
        }
        subtitle={
          estimate ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <Badge data-testid="estimate-status" variant={getStatusVariant(estimate.status)}>
                {getStatusText(estimate.status)}
              </Badge>
              <div data-testid="estimate-meta" className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <User className="h-4 w-4" />
                  <span data-testid="estimate-author">{estimate.created_by_name || 'Неизвестно'}</span>
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  <span data-testid="estimate-date">{formatDate(estimate.created_at)}</span>
                </span>
              </div>
            </div>
          ) : undefined
        }
        breadcrumbs={
          estimate
            ? [
                { label: 'Главная', href: '/', 'data-testid': 'breadcrumb-home' },
                { label: 'Клиенты', href: '/clients', 'data-testid': 'breadcrumb-clients' },
                {
                  label: estimate.query.substring(0, 50) + (estimate.query.length > 50 ? '...' : ''),
                  'data-testid': 'breadcrumb-current',
                },
              ]
            : undefined
        }
        backButton={{ onClick: handleBack }}
        stickyHeader={false}
        >
        {isLoading ? (
          <div data-testid="estimate-detail-loading" className="flex items-center justify-center h-64">
            <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : isError ? (
          <div data-testid="estimate-detail-error" className="flex flex-col items-center justify-center h-64">
            <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
            <p className="text-gray-600 mb-2">Ошибка загрузки деталей сметы.</p>
            <p className="text-sm text-gray-500 mb-4">
              {error instanceof Error ? error.message : 'Неизвестная ошибка'}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" data-testid="back-button-error" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />Назад
              </Button>
              <Button data-testid="retry-button" onClick={() => refetch()}>
                Повторить
              </Button>
            </div>
          </div>
        ) : !estimate ? (
          <div data-testid="estimate-not-found" className="flex flex-col items-center justify-center h-64">
            <FileText className="h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-600 mb-4">Смета не найдена</p>
            <Button variant="outline" data-testid="back-button-not-found" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />Назад
            </Button>
          </div>
        ) : (
          <>
            {/* Brief Chat - TASK-098 */}
            {estimate.messages && estimate.messages.length > 0 && (
              <div className="mb-6">
                <EstimateBriefChat estimateId={estimate.id} messages={estimate.messages} />
              </div>
            )}

            {/* E-commerce order view (TASK-271, AC-7): replaces the estimate
                sections (work groups, sources, overhead) with an order table. */}
            {estimate.mode === 'ecommerce' ? (
              <EstimateOrderView
                estimate={estimate}
                managerControl={
                  <EstimateManagerSelect
                    estimateId={estimate.id}
                    manager={estimate.manager}
                    createdBy={estimate.created_by}
                  />
                }
              />
            ) : (
              <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Cost Summary Card */}
              <Card data-testid="cost-summary-card" className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-lg">Итого</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Общая стоимость</p>
                      <p data-testid="total-cost" className="text-3xl font-bold text-gray-900">
                        {formatCost(estimate.total_cost)} ₽
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Позиции</p>
                      <p data-testid="items-count" className="text-xl font-semibold text-gray-900">
                        {lineItems.length} позиций
                      </p>
                    </div>
                    {estimate.updated_at !== estimate.created_at && (
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Обновлено</p>
                        <p data-testid="last-updated" className="text-sm text-gray-700">
                          {formatDate(estimate.updated_at)}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Sources Card */}
              <Card data-testid="sources-card" className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Database className="h-5 w-5" />
                    Источники данных
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {estimate.sources && estimate.sources.length > 0 ? (
                    <ul data-testid="sources-list" className="space-y-2">
                      {estimate.sources.map((source, index) => (
                        <li
                          key={index}
                          data-testid={`source-${index}`}
                          className="flex items-center gap-2 text-sm text-gray-700">
                          <span className="w-2 h-2 bg-blue-500 rounded-full" />
                          {typeof source === 'object' && source !== null
                            ? (source as { name: string; type?: string }).name
                            : source}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p data-testid="no-sources" className="text-sm text-gray-500">
                      Нет источников данных для этой сметы.
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Line Items Table */}
            <Card data-testid="line-items-card" className="mt-6">
              <CardHeader>
                <CardTitle className="text-lg">Позиции сметы</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {lineItems.length > 0 ? (
                  <>
                    {/* Desktop table */}
                    <div className="hidden md:block overflow-x-auto">
                      <Table data-testid="line-items-table">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">#</TableHead>
                            <TableHead>Наименование</TableHead>
                            <TableHead>Описание</TableHead>
                            <TableHead className="text-right">Ед.</TableHead>
                            <TableHead className="text-right">Кол-во</TableHead>
                            <TableHead className="text-right">Цена за ед.</TableHead>
                            <TableHead className="text-right">Итого</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {lineItems.map((item, index) => (
                            <TableRow key={index} data-testid={`line-item-${index}`}>
                              <TableCell className="text-gray-500">{index + 1}</TableCell>
                              <TableCell data-testid={`item-name-${index}`} className="font-medium">
                                {item.name}
                              </TableCell>
                              <TableCell
                                data-testid={`item-description-${index}`}
                                className="text-gray-500 max-w-xs truncate">
                                {item.description || '-'}
                              </TableCell>
                              <TableCell data-testid={`item-unit-${index}`} className="text-right">
                                {item.unit}
                              </TableCell>
                              <TableCell data-testid={`item-quantity-${index}`} className="text-right">
                                {item.quantity}
                              </TableCell>
                              <TableCell data-testid={`item-unit-price-${index}`} className="text-right">
                                {formatCost(item.unit_price)}
                              </TableCell>
                              <TableCell data-testid={`item-total-${index}`} className="text-right font-medium">
                                {formatCost(item.total_price)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                        <TableFooter>
                          <TableRow>
                            <TableCell colSpan={6} className="text-right font-bold">
                              Итого
                            </TableCell>
                            <TableCell data-testid="table-total" className="text-right font-bold">
                              {formatCost(calculatedTotal)} ₽
                            </TableCell>
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </div>

                    {/* Mobile card view */}
                    <div className="space-y-3 p-4 md:hidden">
                      {lineItems.map((item, index) => (
                        <div key={index} data-testid={`line-item-mobile-${index}`} className="border rounded-lg p-3">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <span className="text-xs text-gray-400">#{index + 1}</span>
                            <span className="text-sm font-semibold text-gray-900 text-right">
                              {formatCost(item.total_price)} ₽
                            </span>
                          </div>
                          <p className="text-sm font-medium text-gray-900 mb-1">{item.name}</p>
                          <p className="text-xs text-gray-500">
                            {item.quantity} {item.unit} &times; {formatCost(item.unit_price)} ₽
                          </p>
                        </div>
                      ))}
                      <div className="border-t pt-3 flex items-center justify-between">
                        <span className="font-bold text-gray-900">Итого</span>
                        <span data-testid="mobile-total" className="font-bold text-gray-900">
                          {formatCost(calculatedTotal)} ₽
                        </span>
                      </div>
                    </div>
                  </>
                ) : lineItems.length === 0 && estimate.messages && estimate.messages.length > 0 ? (
                  <div
                    data-testid="estimate-pending"
                    className="flex flex-col items-center justify-center py-12">
                    <FileText className="h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-500">Смета будет сформирована после уточнения деталей</p>
                  </div>
                ) : (
                  <div
                    data-testid="no-line-items"
                    className="flex flex-col items-center justify-center py-12">
                    <FileText className="h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-500">Нет позиций в этой смете.</p>
                  </div>
                )}
              </CardContent>
            </Card>
              </>
            )}
          </>
        )}
      </PageLayout>
    </>
  );
}

export default EstimateDetailPage;
